import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Preferences } from '@capacitor/preferences';
import { v4 as uuidv4 } from 'uuid';

const isDev = import.meta.env.DEV;

let cachedDeviceId: string | null = null;
let deviceIdPromise: Promise<string> | null = null;

export async function secureSetItem(key: string, value: string) {
  if (Capacitor.isNativePlatform()) {
    try {
      await SecureStoragePlugin.set({ key, value });
    } catch (error) {
      isDev && console.error(`Error setting secure storage item "${key}": ${error}`);
    }
  }
}

export async function secureRemoveItem(key: string) {
  if (Capacitor.isNativePlatform()) {
    try {
      const { value } = await SecureStoragePlugin.remove({ key });
      return value;
    } catch (error) {
      isDev && console.error(`Error removing secure storage item "${key}": ${error}`);
      return null;
    }
  }
  return null;
}

export async function secureGetItem(key: string) {
  if (Capacitor.isNativePlatform()) {
    try {
      const { value } = await SecureStoragePlugin.get({ key });
      return value && value.length ? value : null;
    } catch (error) {
      isDev && console.error(`Error getting secure storage item "${key}": ${error}`);
      return null;
    }
  }
  return null;
}

export async function getDeviceId() {
  if (cachedDeviceId) return cachedDeviceId;

  // Prevent race conditions on cold start
  if (deviceIdPromise) return deviceIdPromise;

  const key = 'deviceId';

  deviceIdPromise = (async () => {
    // Native (iOS / Android)
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key });

      if (value && value.length) {
        cachedDeviceId = value;
        return value;
      }

      const newDeviceId = uuidv4();
      await Preferences.set({ key, value: newDeviceId });
      cachedDeviceId = newDeviceId;
      return newDeviceId;
    }

    const existing = window.localStorage.getItem(key);
    if (existing) {
      cachedDeviceId = existing;
      return existing;
    }

    const newDeviceId = uuidv4();
    window.localStorage.setItem(key, newDeviceId);
    cachedDeviceId = newDeviceId;
    return newDeviceId;
  })();

  try {
    return await deviceIdPromise;
  } finally {
    deviceIdPromise = null;
  }
}
